import type { JSX, Ref } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import { BaseSelect, Button, Popover, type SelectItem } from '~/components/granular';
import { useElementSize } from '~/hooks/useElementSize';
import type { DropdownSelectProps } from './DropdownSelect.props';

export function DropdownSelectComponent<TItemValue>(
  {
    appearance = 'filter',
    applyLabel = 'Apply',
    backLabel = 'Back',
    children,
    disabled,
    disableFiltering,
    indeterminateItems,
    items = [],
    label,
    leftIcon,
    loading,
    noDataLabel = 'No data',
    placeholder,
    searchPlaceholder,
    selectedItems = [],
    selectedLimit,
    size = 'small',
    trigger,
    width,
    onSelectChange,
  }: DropdownSelectProps<TItemValue>,
  ref: Ref<HTMLDivElement>,
) {
  // The trigger element is held in state (via a callback ref) so a custom `trigger` render prop never receives a ref
  const [triggerElement, setTriggerElement] = useState<HTMLDivElement | null>(null);
  const observedTrigger = useMemo(() => ({ current: triggerElement }), [triggerElement]);
  const { width: elementWidth } = useElementSize(observedTrigger);

  const [isOpen, setIsOpen] = useState(false);

  function handleToggleOpen(isOpen: boolean) {
    setIsOpen(isOpen);
  }

  function handleSelectChange(selectedItems: Array<SelectItem<TItemValue>>) {
    onSelectChange?.(selectedItems);
    setIsOpen(false);
  }

  const popoverTrigger = useMemo(() => {
    if (trigger) {
      return trigger(setTriggerElement);
    }
    return (
      <div className="tw-inline-flex" ref={setTriggerElement}>
        <Button appearance={appearance} disabled={disabled} leftIcon={leftIcon} rightIcon="ArrowDownSIcon" size={size}>
          {label || <span className="text-neutral-600">{placeholder}</span>}
        </Button>
      </div>
    );
  }, [trigger, appearance, disabled, size, leftIcon, label, placeholder]);

  return (
    <BaseSelect
      applyLabel={applyLabel}
      backLabel={backLabel}
      disableFiltering={disableFiltering}
      indeterminateItems={indeterminateItems}
      items={items}
      loading={loading}
      noDataLabel={noDataLabel}
      onSelectChange={handleSelectChange}
      ref={ref}
      searchPlaceholder={searchPlaceholder}
      selectedItems={selectedItems}
      selectedLimit={selectedLimit}
      toggleOpen={handleToggleOpen}
      width={width ?? elementWidth}
    >
      <Popover
        align="start"
        contentWidth={width ?? elementWidth}
        direction="bottom"
        disabled={disabled}
        onTriggerClick={(open) => handleToggleOpen(open)}
        open={isOpen}
        trigger={popoverTrigger}
      >
        {children}
      </Popover>
    </BaseSelect>
  );
}

export const DropdownSelect = forwardRef(DropdownSelectComponent) as <T>(
  props: DropdownSelectProps<T> & { ref?: Ref<HTMLDivElement> },
) => JSX.Element;
